/**
 * FIELD engine (Faz 8.8, OCS field.rs parity):
 *
 * Codes live inline in TEXT/MTEXT content as `%<...>%`. Resolution happens
 * into a CACHE store (useFieldCacheStore) — entity data keeps the code, so
 * automatic triggers never mutate the document (no undo spam, no regen
 * loops) and renderers substitute the cached value live.
 *
 * Code families (OCS resolve()):
 *  - \AcVar <name>            system variables: dwgname, date/today/cdate,
 *                             textsize, dimscale/cannoscale, clayer, userr1-5
 *  - \AcExpr (<formula>)      arithmetic (+ - * / parens) over numbers and
 *                             the Faz 6 parameter table values
 *  - \AcObjProp Object(<id>).<Prop>  live object properties (Length, Area,
 *                             Radius, Diameter, Circumference, StartPoint…)
 *
 * Triggers — FIELDEVAL mask (default 31): 1 open, 2 save, 4 plot,
 * 8 eTransmit, 16 regen (debounced 500 ms); 32 = explicit UPDATEFIELD
 * (always honored). FIELDDISPLAY gates the grey-box indicator.
 */

import { create } from 'zustand';
import { useAppStore, type Entity } from '../../state/store';
import { useSystemVarsStore } from '../../state/useSystemVarsStore';
import { useDocumentStore } from '../../state/useDocumentStore';
import { entityToPolyline } from './runners/lib';
import { polygonArea } from './geometryMath';
import { globalParameterManager } from '../services';

export type FieldEvent = 'open' | 'save' | 'plot' | 'etransmit' | 'regen' | 'explicit';

/** FIELDEVAL bit per event (32 = explicit UPDATEFIELD). */
const EVENT_BITS: Record<FieldEvent, number> = {
    open: 1, save: 2, plot: 4, etransmit: 8, regen: 16, explicit: 32,
};

/** Resolved-value cache: entityId+code → last resolved text. */
interface FieldCacheState {
    values: Record<string, string>;
    put: (key: string, value: string) => void;
}
export const useFieldCacheStore = create<FieldCacheState>((set) => ({
    values: {},
    put: (key, value) => set((s) => (s.values[key] === value ? s : { values: { ...s.values, [key]: value } })),
}));

const cacheKey = (id: string, code: string): string => `${id}:${code}`;

/** Extract `%<...>%` codes from a text. */
export function fieldCodesOf(content: string): string[] {
    const out: string[] = [];
    const simple = /%<(.*?)%>/g;
    let m: RegExpExecArray | null;
    while ((m = simple.exec(content)) !== null) out.push(m[1].trim());
    return out;
}

/** True when the content carries at least one field code. */
export function hasFieldCodes(content: string): boolean {
    return /%<.*?%>/.test(String(content ?? ''));
}

/** Format a numeric result with the optional `\f "%lu<digits>"` suffix. */
function formatNumber(n: number, code: string): string {
    const fm = /\\f\s*"%lu(\d+)?"/.exec(code);
    const digits = fm?.[1] !== undefined ? parseInt(fm[1], 10) : 2;
    return n.toFixed(Math.max(0, Math.min(8, digits)));
}

/** Document surface values (AcVar parity subset). */
function resolveAcVar(name: string, code: string): string | null {
    const doc = useDocumentStore.getState();
    const sys = useSystemVarsStore.getState();
    const app = useAppStore.getState();
    const key = name.toLowerCase();
    switch (key) {
        case 'dwgname':
            return doc.displayName?.replace(/\.[^.]+$/, '') ?? '';
        case 'dwgprefix':
            return '';
        case 'clayer':
            return app.layers[app.activeLayerId]?.name ?? '';
        case 'textsize':
            return formatNumber(0.3, code);
        case 'dimscale':
        case 'cannoscale':
            return String(Math.round(sys.CANNOSCALE));
        case 'date':
        case 'today':
            return new Date().toLocaleDateString();
        case 'cdate':
        case 'savedate':
            return new Date().toLocaleString();
        case 'dwgtitle':
            return doc.displayName ?? '';
        default:
            // userr1-5 pass-through (0 when unset — no registry in yap).
            if (/^userr[1-5]$/.test(key)) return formatNumber(0, code);
            return null;
    }
}

/** Safe arithmetic evaluator (+ - * / and parentheses, numbers only). */
function evalFormula(expr: string): number | null {
    if (!/^[-+*/(). 0-9]+$/.test(expr)) return null;
    try {
        // eslint-disable-next-line no-new-func
        const fn = new Function(`return (${expr});`) as () => unknown;
        const v = fn();
        return typeof v === 'number' && Number.isFinite(v) ? v : null;
    } catch {
        return null;
    }
}

/** Live object properties (AcObjProp parity subset). */
function resolveObjectProp(entity: Entity, prop: string): string | null {
    const d = entity.data as Record<string, any>;
    const p = prop.trim().toLowerCase();
    const poly = entityToPolyline(entity);
    switch (entity.type) {
        case 'line': {
            if (p === 'length' && d.start && d.end) {
                return formatNumber(Math.hypot(d.end[0] - d.start[0], d.end[1] - d.start[1]), prop);
            }
            if (p === 'startpoint') return `${d.start?.[0]?.toFixed(2)},${d.start?.[1]?.toFixed(2)}`;
            if (p === 'endpoint') return `${d.end?.[0]?.toFixed(2)},${d.end?.[1]?.toFixed(2)}`;
            return null;
        }
        case 'circle': {
            if (p === 'radius') return formatNumber(d.radius, prop);
            if (p === 'diameter') return formatNumber(d.radius * 2, prop);
            if (p === 'area') return formatNumber(Math.PI * d.radius * d.radius, prop);
            if (p === 'circumference') return formatNumber(2 * Math.PI * d.radius, prop);
            return null;
        }
        case 'arc': {
            if (p === 'radius') return formatNumber(d.radius, prop);
            if (p === 'length' && typeof d.startAngle === 'number') {
                const sweep = Math.max(0, (d.endAngle ?? 0) - d.startAngle);
                return formatNumber(d.radius * sweep, prop);
            }
            return null;
        }
        case 'hatch':
        case 'polyline':
        case 'rectangle':
        case 'polygon': {
            if (p === 'area' && poly && poly.length >= 3) return formatNumber(polygonArea(poly), prop);
            if (p === 'length' && poly) {
                let len = 0;
                for (let i = 0; i < poly.length - 1; i++) {
                    len += Math.hypot(poly[i + 1][0] - poly[i][0], poly[i + 1][1] - poly[i][1]);
                }
                return formatNumber(len, prop);
            }
            return null;
        }
        default:
            return null;
    }
}

/** Resolve one field code to text (null = unresolvable — cache preserved). */
export function resolveFieldCode(codeRaw: string, hostEntityId: string | null): string | null {
    const code = codeRaw.trim().replace(/^\\_/, '\\').replace(/\\_/g, '\\');
    if (code.startsWith('\\AcVar')) {
        const name = code.slice('\\AcVar'.length).trim().split(/\s+/)[0] ?? '';
        return resolveAcVar(name, code);
    }
    if (code.startsWith('\\AcExpr')) {
        let expr = code.slice('\\AcExpr'.length).trim();
        // Object references inside formulas have no ObjIdx table in yap —
        // they resolve to neutral 0 (OCS resolve(context) note).
        expr = expr.replace(/%<_?\\?_?ObjIdx\s+(\d+)%>/gi, '0');
        // Parameter-table identifiers (Faz 6 bridge) inline their values.
        expr = expr.replace(/[A-Za-z_][A-Za-z0-9_]*/g, (name) => {
            const v = parameterValue(name);
            return v === null ? '0' : String(v);
        });
        const v = evalFormula(expr.replace(/^\(|\)$/g, ''));
        return v === null ? null : formatNumber(v, code);
    }
    const obj = /^\\AcObjProp\s+Object\((.*?)\)\.(.+)$/.exec(code);
    if (obj) {
        const targetId = obj[1].trim();
        const app = useAppStore.getState();
        const e = app.entities[targetId] ?? (hostEntityId ? app.entities[hostEntityId] : undefined);
        if (!e) return null;
        return resolveObjectProp(e, obj[2]);
    }
    if (code.startsWith('\\AcDiesel') || code.startsWith('\\AcSm')) return null;
    return null;
}

/** Entities whose content carries field codes (hosts). */
function fieldHosts(): Entity[] {
    const { entities } = useAppStore.getState();
    return Object.values(entities).filter((e) => (e.type === 'text' || e.type === 'mtext') && hasFieldCodes(String(e.data.content ?? '')));
}

/**
 * Evaluate every host's field codes for an event (FIELDEVAL mask gate).
 * Unresolved codes keep their previous cache (OCS None → cache kept).
 * Returns the number of host entities refreshed.
 */
export function updateFields(event: FieldEvent): number {
    const bit = EVENT_BITS[event];
    const mask = useSystemVarsStore.getState().FIELDEVAL;
    if (event !== 'explicit' && !(mask & bit)) return 0;
    const hosts = fieldHosts();
    for (const host of hosts) {
        for (const code of fieldCodesOf(String(host.data.content ?? ''))) {
            const resolved = resolveFieldCode(code, host.id);
            if (resolved !== null) useFieldCacheStore.getState().put(cacheKey(host.id, code), resolved);
        }
    }
    return hosts.length;
}

/** Render-time substitution: codes → cached value or the FIELDDISPLAY box. */
export function renderFieldText(content: string, entityId: string): string {
    if (!hasFieldCodes(content)) return content;
    const display = useSystemVarsStore.getState().FIELDDISPLAY !== 0;
    return content.replace(/%<(.*?)%>/g, (_m, code: string) => {
        const cached = useFieldCacheStore.getState().values[cacheKey(entityId, code.trim())];
        if (cached !== undefined) return cached;
        if (display) return '▮';
        // FIELDDISPLAY off: the code itself shows (AutoCAD shows the raw code).
        return `%<${code.trim()}%>`;
    });
}

/** Debounced regen trigger state. */
let regenTimer: number | null = null;
let fieldsEngineStarted = false;

/**
 * Engine bootstrap: the 'open' trigger fires once, then a debounced (500 ms)
 * entity-watcher drives the regen trigger. Large drawings stay cheap:
 * only entities whose content carries %<…>% codes are scanned.
 */
export function startFieldEngine(): void {
    if (fieldsEngineStarted) return;
    fieldsEngineStarted = true;
    window.setTimeout(() => { updateFields('open'); }, 0);
    let prevEntities: unknown = null;
    useAppStore.subscribe((state) => {
        // Plain subscribe (no selector middleware): compare the entities
        // reference and skip the boot emit so 'open' is the first event.
        if (prevEntities === null) { prevEntities = state.entities; return; }
        if (prevEntities === state.entities) return;
        prevEntities = state.entities;
        if (!(useSystemVarsStore.getState().FIELDEVAL & 16)) return;
        if (regenTimer !== null) window.clearTimeout(regenTimer);
        regenTimer = window.setTimeout(() => {
            regenTimer = null;
            updateFields('regen');
        }, 500);
    });
}

/** Parameter-table values for \AcExpr (Faz 6 bridge). */
export function parameterValue(name: string): number | null {
    try {
        const param = globalParameterManager.getParameter(name);
        if (!param) return null;
        const n = typeof param.value === 'number' ? param.value : Number(param.value);
        return Number.isFinite(n) ? n : null;
    } catch {
        return null;
    }
}
